import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AuditLogResponse,
  AuditCatalogResponse,
  AuditAuthResponse,
  UserTimelineResponse,
  AuditSettingResponse,
  AuditSettingUpdateRequest,
  AuditLogSearchParams,
  AuditAuthSearchParams,
  AuditTimelineParams,
  PageResponse
} from '../models/audit.model';

@Injectable({
  providedIn: 'root'
})
export class AuditApiService {
  private base = `${environment.apiUrl}api/audit`;

  constructor(private http: HttpClient) {}

  // ── Audit Logs ──

  getLogs(params: AuditLogSearchParams = {}): Observable<PageResponse<AuditLogResponse>> {
    return this.http.get<PageResponse<AuditLogResponse>>(`${this.base}/logs`, {
      params: this.buildParams(params)
    });
  }

  // ── Filter Catalog ──

  /** Valores distintos para poblar los filtros (rol Manager+). businessId primero. */
  getCatalog(businessId: number): Observable<AuditCatalogResponse> {
    const params = new HttpParams().set('businessId', String(businessId));
    return this.http.get<AuditCatalogResponse>(`${this.base}/logs/catalog`, { params });
  }

  // ── Entity Timeline ──

  getEntityTimeline(
    entityName: string,
    entityId: string,
    params: AuditTimelineParams = {}
  ): Observable<PageResponse<AuditLogResponse>> {
    return this.http.get<PageResponse<AuditLogResponse>>(
      `${this.base}/logs/${entityName}/${entityId}/timeline`,
      { params: this.buildParams(params) }
    );
  }

  /**
   * Historial del REGISTRO, vía el alias `/logs/entity/...`.
   *
   * <p>Idéntico a {@link getEntityTimeline} salvo por la ruta. Hace falta cuando el nombre de
   * entidad choca con una ruta literal del backend: para `user`, la ruta genérica queda
   * ensombrecida por `/logs/user/{userId}/timeline`, que responde otra cosa — las operaciones
   * que ESE usuario ejecutó (y sus logins), no los cambios sobre su cuenta — con un DTO
   * distinto y sin diffs por campo.</p>
   */
  getRecordTimeline(
    entityName: string,
    entityId: string,
    params: AuditTimelineParams = {}
  ): Observable<PageResponse<AuditLogResponse>> {
    return this.http.get<PageResponse<AuditLogResponse>>(
      `${this.base}/logs/entity/${entityName}/${entityId}/timeline`,
      { params: this.buildParams(params) }
    );
  }

  // ── User Timeline ──

  getUserTimeline(
    userId: number,
    params: AuditTimelineParams = {}
  ): Observable<PageResponse<UserTimelineResponse>> {
    return this.http.get<PageResponse<UserTimelineResponse>>(
      `${this.base}/logs/user/${userId}/timeline`,
      { params: this.buildParams(params) }
    );
  }

  // ── Auth Logs ──

  getAuthLogs(params: AuditAuthSearchParams = {}): Observable<PageResponse<AuditAuthResponse>> {
    return this.http.get<PageResponse<AuditAuthResponse>>(`${this.base}/auth-logs`, {
      params: this.buildParams(params)
    });
  }

  // ── Settings ──

  getSettings(): Observable<AuditSettingResponse[]> {
    return this.http.get<AuditSettingResponse[]>(`${this.base}/settings`);
  }

  getSettingByEntity(entityName: string): Observable<AuditSettingResponse> {
    return this.http.get<AuditSettingResponse>(`${this.base}/settings/${entityName}`);
  }

  updateSetting(entityName: string, req: AuditSettingUpdateRequest): Observable<AuditSettingResponse> {
    return this.http.put<AuditSettingResponse>(`${this.base}/settings/${entityName}`, req);
  }

  reloadSettingsCache(): Observable<void> {
    return this.http.post<void>(`${this.base}/settings/reload`, null);
  }

  // ── Helper ──

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildParams(obj: any): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(obj)) {
      if (value != null && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return params;
  }
}
